import axios from "axios";
import React from "react";
import { useEffect, useState } from "react";

import Paper from "@mui/material/Paper";
import { Stack } from "@mui/material";

import DetailsTable from "./DetailsTable";
import GetCSRFToken from "../../AuthComponents/GetCSRFToken";
import StockPlan from "./../../StockPlanTypes/StockPlan";
import StockPlanDialog from "./StockPlanDialog";

import ErrorPage from "../../ErrorPageComponents/ErrorPage";

import FormRowState from "../UtilityComponents/FormUtilityComponents/FormRowState";
import FormConfig from "../UtilityComponents/FormUtilityComponents/FormConfig";

import SymbolOption from "../UtilityComponents/SymbolUtilityComponents/SymbolOption";
import DetailsStats from "./DetailsStats";

const paperPadding = 2;

function outProcessStockPlanParams(spp: FormRowState[], old_name?: string) {
  return {
    params: spp,
    old_name: old_name,
    // startDate: spp.startDate === null ? dayjs() : spp.startDate.format(IsoDateOnlyFormat),
  };
}

type DetailsPanelProps = {
  // setConfirmOnNavigateAway: (confirmOnNavigateAway: Partial<ConfirmDialogProps> | false) => void;
  stockPlans: StockPlan[];
  stockPrice: number | undefined;
  statsError: boolean;
  lastDrawnReferencePrice: number | undefined;
  setLastDrawnReferencePrice: (value: number) => void;
  referenceDateString: string;
  handleUpdateNotification: () => void;
  handleSymbolAdd: () => void;
  dialogOpen: boolean;
  setDialogOpen: (open: boolean) => void;
  stockPlanError: boolean;
  userSymbolOption: SymbolOption | undefined;
  vestedValue: number;
  vestedValueGross: number;
  lastDrawnVestedValue: number | undefined;
  setLastDrawnVestedValue: (value: number) => void;
  totalStockValue: number;
  totalStockValueGross: number;
  lastDrawnTotalStockValue: number | undefined;
  setLastDrawnTotalStockValue: (value: number) => void;
  percentVested: number;
  lastDrawnPercentVested: number | undefined;
  setLastDrawnPercentVested: (value: number) => void;
  totalSharesSold: number;
  lastDrawnSharesSold: number | undefined;
  setLastDrawnSharesSold: (value: number) => void;
  daysUntilNextVest: number | null;
  lastDrawnDaysUntilNextVest: number | undefined;
  setLastDrawnDaysUntilNextVest: (value: number) => void;
  firstCheckDone: boolean;
  promptForSymbol: boolean;
  finishPromptForSymbol: () => void;
  firstUserSymbolCheckDone: boolean;
  stockPlanSelectorConfig: FormConfig | undefined; // This is the form config for the form input
  setStockPlanSelectorConfig: (
    newConfig: (FormConfig | undefined) | ((oldConfig: FormConfig | undefined) => FormConfig | undefined)
  ) => void;
  stockPlanSelectorState: FormRowState[];
  defaultStockPlanSelectState: FormRowState[]; // This is the form config we default to
  defaultStockPlanSelectorForm: FormConfig | undefined;
  setStockPlanSelectorState: (newState: FormRowState[] | ((oldState: FormRowState[]) => FormRowState[])) => void;
  externalStartAdd: boolean;
  clearExternalStartAdd: () => void;
  withholdingOn: boolean;
  withholdingRate: number;
  firstWithholdingCheckDone: boolean;
};

function DetailsPanel(props: DetailsPanelProps) {
  const [loading, setLoading] = useState(false);
  const [dialogIsNew, setDialogIsNew] = useState(true);
  const [oldName, setOldName] = useState<string | undefined>(undefined);
  const [resetToDefaultOnAdd, setResetToDefaultOnAdd] = useState(true);
  const [symbol, setSymbol] = useState<string | undefined>(
    props.userSymbolOption === undefined ? undefined : props.userSymbolOption.symbol
  );
  const [symbolUnsubmitted, setSymbolUnsubmitted] = useState(props.userSymbolOption === undefined);

  const [showNet, setShowNet] = useState(true);

  const dialogOpen = props.dialogOpen;
  const setDialogOpen = props.setDialogOpen;

  function addSymbol(symbol: string) {
    setLoading(true);
    axios
      .post(
        "/api/stockplan/symbol",
        {
          symbol: symbol.toUpperCase(),
        },
        {
          headers: { "X-CSRFToken": GetCSRFToken() },
        }
      )
      .then((res) => {
        props.handleSymbolAdd();
        setSymbolUnsubmitted(false);
        setLoading(false);
      })
      .catch((error) => {
        if (process.env.NODE_ENV === "development") {
          console.log(error);
        }
        // TODO: surface an error for symbol add
        setLoading(false);
      });
  }

  function startAddStockPlan() {
    if (props.stockPlanSelectorConfig === undefined || !props.firstUserSymbolCheckDone) {
      return;
    }
    if (resetToDefaultOnAdd) {
      setResetToDefaultOnAdd(false);
      setOldName(undefined);
      props.setStockPlanSelectorState(props.defaultStockPlanSelectState);
      props.setStockPlanSelectorConfig(props.defaultStockPlanSelectorForm);
    }
    setDialogIsNew(true);
    setDialogOpen(true);
  }

  useEffect(() => {
    if (props.externalStartAdd) {
      if (!dialogOpen) {
        startAddStockPlan();
      }
      props.clearExternalStartAdd();
    }
  }, [props.externalStartAdd, props.clearExternalStartAdd]);

  function addStockPlan(newStockPlanParams: FormRowState[]) {
    setLoading(true);
    axios
      .post("/api/stockplan", outProcessStockPlanParams(newStockPlanParams), {
        headers: { "X-CSRFToken": GetCSRFToken() },
      })
      .then((res) => {
        props.handleUpdateNotification();
        setResetToDefaultOnAdd(true);
        setLoading(false);
        setDialogOpen(false);
        setTimeout(() => props.setStockPlanSelectorConfig(props.defaultStockPlanSelectorForm), 500); // delay by half a second to allow the dialog to close
        if (props.promptForSymbol) {
          setTimeout(() => props.finishPromptForSymbol(), 500); // delay by half a second to allow the dialog to close
        }
      })
      .catch((error) => {
        if (error.response.status === 500) {
          if (process.env.NODE_ENV === "development") {
            console.log(error.response);
          } // TODO: instead of closing dialogue communicate about the error
          setDialogOpen(false);
        } else if (error.response.data && "rowStates" in error.response.data) {
          props.setStockPlanSelectorConfig({ ...error.response.data, rowStates: undefined });
          props.setStockPlanSelectorState(error.response.data.rowStates);
        } else {
          if (process.env.NODE_ENV === "development") {
            console.log(error.response);
          } // TODO: instead of closing dialogue communicate about the error
          setDialogOpen(false);
        }
        setLoading(false);
      });
  }

  function updateStockPlan(newStockPlanParams: FormRowState[], stockPlanEdited: boolean, old_name?: string) {
    setLoading(true);
    if (stockPlanEdited) {
      axios
        .post("/api/stockplan/update", outProcessStockPlanParams(newStockPlanParams, old_name), {
          headers: { "X-CSRFToken": GetCSRFToken() },
        })
        .then((res) => {
          props.handleUpdateNotification();
          setDialogOpen(false);
        })
        .catch((error) => {
          if (error.response.status === 500) {
            if (process.env.NODE_ENV === "development") {
              console.log(error.response);
            } // TODO: instead of closing dialogue communicate about the error
            setDialogOpen(false);
          } else if (error.response.data && "rowStates" in error.response.data) {
            props.setStockPlanSelectorConfig({ ...error.response.data, rowStates: undefined });
            props.setStockPlanSelectorState(error.response.data.rowStates);
          } else {
            if (process.env.NODE_ENV === "development") {
              console.log(error.response);
            } // TODO: instead of closing dialogue communicate about the error
            setDialogOpen(false);
          }
        })
        .finally(() => {
          setLoading(false);
        });
    } else {
      setLoading(false);
      setDialogOpen(false);
    }
  }

  function deleteStockPlan(name: string | undefined) {
    if (name !== undefined) {
      setLoading(true);
      axios
        .post(
          "/api/stockplan/delete",
          { name: name },
          {
            headers: { "X-CSRFToken": GetCSRFToken() },
          }
        )
        .then((res) => {
          props.handleUpdateNotification();
        })
        .catch((error) => {
          if (process.env.NODE_ENV === "development") {
            console.log(error.response);
          } // TODO: error handle this
        })
        .finally(() => {
          setLoading(false);
          setDialogOpen(false);
        });
    }
  }

  function setStockPlanFormStateFromName(name: string) {
    const stockPlan = props.stockPlans.find((sp) => sp.rowStates[0].currentModeStates[0] === name);
    if (stockPlan) {
      setOldName(name);
      props.setStockPlanSelectorConfig(stockPlan.formConfig);
      props.setStockPlanSelectorState(stockPlan.rowStates);
    } // TODO: gracefully handle the else case?
  }

  const [stockPlanEdited, setStockPlanEdited] = useState(false); // TODO: still hits backend if params changed and then changed back

  return (
    <Stack sx={{ gap: 2 }}>
      <Paper
        sx={{
          flexGrow: 0,
          padding: paperPadding,
        }}
      >
        <DetailsStats
          noStockPlans={props.stockPlans.length === 0}
          statsError={props.statsError}
          totalStockValue={showNet ? props.totalStockValue : props.totalStockValueGross}
          lastDrawnTotalStockValue={props.lastDrawnTotalStockValue}
          setLastDrawnTotalStockValue={props.setLastDrawnTotalStockValue}
          vestedValue={showNet ? props.vestedValue : props.vestedValueGross}
          lastDrawnVestedValue={props.lastDrawnVestedValue}
          setLastDrawnVestedValue={props.setLastDrawnVestedValue}
          sharesSold={props.totalSharesSold}
          lastDrawnSharesSold={props.lastDrawnSharesSold}
          setLastDrawnSharesSold={props.setLastDrawnSharesSold}
          pctVested={props.percentVested}
          lastDrawnPctVested={props.lastDrawnPercentVested}
          setLastDrawnPctVested={props.setLastDrawnPercentVested}
          daysUntilNextVest={props.daysUntilNextVest}
          lastDrawnDaysUntilNextVest={props.lastDrawnDaysUntilNextVest}
          setLastDrawnDaysUntilNextVest={props.setLastDrawnDaysUntilNextVest}
          userSymbol={props.userSymbolOption === undefined ? "" : props.userSymbolOption.symbol}
          referencePrice={props.stockPrice === undefined ? 0 : props.stockPrice}
          lastDrawnReferencePrice={props.lastDrawnReferencePrice}
          setLastDrawnReferencePrice={props.setLastDrawnReferencePrice}
          loading={props.stockPrice === undefined || props.stockPlans === undefined}
          firstCheckDone={props.firstCheckDone}
          startAdd={startAddStockPlan}
          loadingAddStockPlan={!props.firstUserSymbolCheckDone || props.stockPlanSelectorConfig === undefined}
        />
      </Paper>
      {props.stockPlanError ? (
        <Paper
          sx={{
            flexGrow: 1,
            padding: paperPadding,
          }}
        >
          <ErrorPage checkYourInputsandRefresh />
        </Paper>
      ) : (
        <DetailsTable
          stockPlans={props.stockPlans}
          stockPrice={props.stockPrice}
          referenceDateString={props.referenceDateString}
          loadingAddStockPlan={!props.firstUserSymbolCheckDone || props.stockPlanSelectorConfig === undefined}
          loadingTable={props.stockPrice === undefined || props.stockPlans === undefined}
          onStockPlanEdit={(name: string) => {
            setDialogIsNew(false);
            setStockPlanEdited(false);
            setOldName(name);
            setStockPlanFormStateFromName(name);
            setDialogOpen(true);
            setResetToDefaultOnAdd(true);
          }}
          startAdd={startAddStockPlan}
          withholdingOn={props.withholdingOn}
          withholdingRate={props.withholdingRate}
          firstWithholdingCheckDone={props.firstWithholdingCheckDone}
          showNet={showNet}
          setShowNet={setShowNet}
        />
      )}
      {props.stockPlanSelectorConfig === undefined && props.firstUserSymbolCheckDone ? null : (
        <StockPlanDialog
          open={dialogOpen}
          loading={loading}
          handleCancel={() => (loading ? null : setDialogOpen(false))}
          isNew={dialogIsNew}
          handleAddSymbol={addSymbol}
          handleAddOrSave={
            dialogIsNew
              ? addStockPlan
              : (newStockPlan: FormRowState[]) => updateStockPlan(newStockPlan, stockPlanEdited, oldName)
          }
          symbol={symbol}
          setSymbol={setSymbol}
          symbolUnsubmitted={symbolUnsubmitted}
          reeditSymbol={() => setSymbolUnsubmitted(true)}
          promptForSymbol={props.promptForSymbol}
          stockPlanFormConfig={
            props.stockPlanSelectorConfig === undefined
              ? { description: "", formConfig: [] }
              : props.stockPlanSelectorConfig
          }
          setStockPlanFormConfig={props.setStockPlanSelectorConfig}
          stockPlanFormState={props.stockPlanSelectorState}
          setStockPlanFormState={(
            newStockPlanParameters: FormRowState[] | ((oldState: FormRowState[]) => FormRowState[])
          ) => {
            setStockPlanEdited(true);
            props.setStockPlanSelectorState(newStockPlanParameters);
          }}
          handleDelete={() => {
            deleteStockPlan(
              props.stockPlanSelectorState[0].currentModeStates[0] === null
                ? undefined
                : props.stockPlanSelectorState[0].currentModeStates[0].toString()
            );
            setDialogOpen(false);
          }}
        />
      )}
    </Stack>
  );
}

export default DetailsPanel;
